import React, { useState, useEffect } from 'react';
import { User, Lock, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function TenantLogin({ lang = 'mr', slug = 'samarth-krupa-dairy', onLoginSuccess, onNavigate }) {
  const isMr = lang === 'mr';

  const [currentSlug, setCurrentSlug] = useState(slug);
  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Notice if logged out due to concurrent session on another device
  const [concurrentLogoutNotice, setConcurrentLogoutNotice] = useState(() => {
    try {
      return localStorage.getItem('milkpro_concurrent_logout_msg') || null;
    } catch {
      return null;
    }
  });

  // Remember this tenant slug for instant PWA relaunch
  useEffect(() => {
    if (currentSlug) {
      try {
        localStorage.setItem('milkpro_last_tenant_slug', currentSlug);
      } catch (e) {}
    }
  }, [currentSlug]);
  const [error, setError] = useState(null);

  // Fetch this specific dairy's public branding by slug (Zero leakage of other dairies)
  useEffect(() => {
    const fetchDairyInfo = async () => {
      if (!currentSlug) {
        setNotFound(true);
        setLoadingTenant(false);
        return;
      }
      setLoadingTenant(true);
      setNotFound(false);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/tenant/by-slug/${encodeURIComponent(currentSlug)}`);
        const data = await res.json();
        if (data.success && data.tenant) {
          setTenant(data.tenant);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching dairy info:', err);
        setNotFound(true);
      } finally {
        setLoadingTenant(false);
      }
    };

    fetchDairyInfo();
  }, [currentSlug]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/tenant/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug: currentSlug,
          username: username.trim(),
          password: password
        })
      });

      const data = await res.json();
      if (data.success && data.token) {
        localStorage.setItem('milkpro_tenant_token', data.token);
        localStorage.setItem('milkpro_tenant_user', JSON.stringify(data.user));
        localStorage.setItem('milkpro_tenant_data', JSON.stringify(data.tenant));
        if (data.session_token) {
          localStorage.setItem('milkpro_session_token', data.session_token);
        }
        localStorage.removeItem('milkpro_concurrent_logout_msg');
        setConcurrentLogoutNotice(null);
        onLoginSuccess(data.user, data.tenant);
      } else {
        setError(data.message || (isMr ? 'अवैध युझरनेम किंवा पासवर्ड' : 'Invalid username or password'));
      }
    } catch (err) {
      setError((isMr ? 'सर्व्हरशी संपर्क होऊ शकला नाही: ' : 'Cannot reach server: ') + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f0f9ff 0%, #f8fafc 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Back to Home Link */}
      <div style={{ width: '100%', maxWidth: 440, marginBottom: '1.25rem' }}>
        <button
          onClick={() => onNavigate('landing')}
          style={{
            background: 'none',
            border: 'none',
            color: '#0284c7',
            fontSize: '0.85rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: 'pointer',
            padding: 0
          }}
        >
          <ArrowLeft size={16} />
          <span>{isMr ? '← मुख्य पानावर परत जा' : '← Back to Home'}</span>
        </button>
      </div>

      {/* Login Card */}
      <div style={{
        background: '#ffffff',
        borderRadius: 20,
        width: '100%',
        maxWidth: 440,
        padding: '2.25rem',
        boxShadow: '0 10px 30px -5px rgba(2, 132, 199, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0'
      }}>
        
        {loadingTenant ? (
          <div style={{ textAlign: 'center', padding: '2rem 0', color: '#64748b', fontSize: '0.9rem' }}>
            {isMr ? 'डेअरी पोर्टल लोड होत आहे...' : 'Loading dairy portal...'}
          </div>
        ) : notFound ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚠️</div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px 0' }}>
              {isMr ? 'डेअरी कोड सापडला नाही' : 'Dairy Code Not Found'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 1.5rem 0' }}>
              {isMr ? 'कृपया योग्य डेअरी कोड प्रविष्ट करा:' : 'Please enter a valid dairy code:'}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="samarth-krupa-dairy"
                onChange={e => setCurrentSlug(e.target.value)}
                style={{ flex: 1, padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.85rem' }}
              />
              <button
                onClick={() => setCurrentSlug(currentSlug || 'samarth-krupa-dairy')}
                style={{ background: '#0284c7', color: '#fff', border: 'none', padding: '0.6rem 1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
              >
                शोध
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Dedicated Dairy Branding Header */}
            <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
              <div style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.4rem',
                marginBottom: '0.75rem',
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
              }}>
                🥛
              </div>
              <h2 style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0' }}>
                {isMr ? (tenant?.brand_name_mr || tenant?.name) : (tenant?.brand_name_en || tenant?.name)}
              </h2>
              <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                {tenant?.city} • {isMr ? 'कर्मचारी व ऑपरेटर लॉगिन' : 'Staff & Operator Login'}
              </div>
            </div>

            {/* Concurrent Device Logout Security Notice */}
            {concurrentLogoutNotice && (
              <div style={{
                background: '#fff1f2',
                border: '1.5px solid #f43f5e',
                color: '#9f1239',
                padding: '0.85rem 1rem',
                borderRadius: 12,
                fontSize: '0.86rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                marginBottom: '1.25rem',
                boxShadow: '0 4px 12px rgba(244, 63, 94, 0.15)',
                lineHeight: 1.45
              }}>
                <AlertCircle size={22} style={{ flexShrink: 0, marginTop: 2, color: '#e11d48' }} />
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.92rem', marginBottom: 2, color: '#be123c' }}>
                    {isMr ? '⚠️ सुरक्षितता सूचना (Security Notice)' : '⚠️ Security Notice'}
                  </div>
                  <div>{concurrentLogoutNotice}</div>
                </div>
              </div>
            )}

            {/* Error Alert */}
            {error && (
              <div style={{
                background: '#fef2f2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '0.75rem 1rem',
                borderRadius: 10,
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: '1.25rem'
              }}>
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              {/* Username */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  {isMr ? 'युझरनेम *' : 'Username *'}
                </label>
                <div style={{ position: 'relative' }}>
                  <User size={18} color="#64748b" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    required
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder={isMr ? 'तुमचे युझरनेम' : 'Enter username'}
                    style={{
                      width: '100%',
                      padding: '0.75rem 0.75rem 0.75rem 2.4rem',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      background: '#f8fafc',
                      outline: 'none',
                      boxSizing: 'border-box',
                      color: '#0f172a',
                      fontWeight: 600
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#334155', marginBottom: 6 }}>
                  {isMr ? 'पासवर्ड *' : 'Password *'}
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={18} color="#64748b" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    style={{
                      width: '100%',
                      padding: '0.75rem 0.75rem 0.75rem 2.4rem',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.9rem',
                      background: '#f8fafc',
                      outline: 'none',
                      boxSizing: 'border-box',
                      color: '#0f172a'
                    }}
                  />
                </div>
              </div>

              

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '0.85rem',
                  borderRadius: 10,
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.3)'
                }}
              >
                {submitting ? (
                  <span>{isMr ? 'लॉगिन पडताळणी होत आहे...' : 'Verifying...'}</span>
                ) : (
                  <>
                    <span>{isMr ? 'लॉगिन करा' : 'Sign In'}</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>

      
    </div>
  );
}
